import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config/api';
import { 
  FiBarChart2, 
  FiDownload, 
  FiUsers,
  FiZap,
  FiPieChart,
  FiTrendingUp,
  FiRefreshCw,
  FiSun,
  FiCloudOff,
  FiAlertTriangle
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const Reports = () => {
  const [reportType, setReportType] = useState('powerStatus');
  const [dateRange, setDateRange] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const COLORS = {
    activo: '#10B981',
    sin_luz: '#F59E0B',
    desconectado: '#6B7280',
    urgente: '#EF4444',
    traspaso: '#3B82F6'
  };

  useEffect(() => {
    fetchReportData();
  }, [reportType, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Obtener datos de clientes para los reportes
      const clientsResponse = await axios.get(`${API_URL}/api/clients?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const clients = clientsResponse.data.clients || [];
      
      // Generar datos según el tipo de reporte
      let data = [];
      let summary = {};

      switch(reportType) {
        case 'powerStatus':
          // Distribución por estado de luz
          const activo = clients.filter(c => c.powerStatus === 'activo' && !c.urgent).length;
          const sinLuz = clients.filter(c => c.powerStatus === 'sin_luz' && !c.urgent).length;
          const desconectado = clients.filter(c => c.powerStatus === 'desconectado').length;
          const urgente = clients.filter(c => c.urgent === true).length;
          
          data = [
            { name: 'Activo', value: activo, color: '#10B981' },
            { name: 'Sin Luz', value: sinLuz, color: '#F59E0B' },
            { name: 'Desconectado', value: desconectado, color: '#6B7280' },
            { name: 'Urgente', value: urgente, color: '#EF4444' }
          ].filter(item => item.value > 0);
          
          summary = {
            total: clients.length,
            activo,
            sinLuz,
            desconectado,
            urgente
          };
          break;

        case 'companies':
          // Top 10 compañías por número de clientes
          const companyCount = {};
          clients.forEach(c => {
            if (c.company) {
              companyCount[c.company] = (companyCount[c.company] || 0) + 1;
            }
          });
          
          data = Object.entries(companyCount)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);
          
          summary = {
            totalCompanies: Object.keys(companyCount).length,
            topCompany: data[0]?.name || 'N/A',
            topCount: data[0]?.value || 0
          };
          break;

        case 'dueDates':
          // Distribución de vencimientos
          const today = new Date();
          const nextWeek = new Date(today);
          nextWeek.setDate(today.getDate() + 7);
          const nextMonth = new Date(today);
          nextMonth.setMonth(today.getMonth() + 1);
          
          const venceEstaSemana = clients.filter(c => {
            if (!c.dueDate) return false;
            const due = new Date(c.dueDate);
            return due >= today && due <= nextWeek;
          }).length;
          
          const venceEsteMes = clients.filter(c => {
            if (!c.dueDate) return false;
            const due = new Date(c.dueDate);
            return due > nextWeek && due <= nextMonth;
          }).length;
          
          const vencidos = clients.filter(c => {
            if (!c.dueDate) return false;
            const due = new Date(c.dueDate);
            return due < today;
          }).length;
          
          data = [
            { name: 'Vencidos', value: vencidos },
            { name: 'Esta semana', value: venceEstaSemana },
            { name: 'Este mes', value: venceEsteMes }
          ];
          
          summary = {
            vencidos,
            proximos: venceEstaSemana + venceEsteMes,
            total: clients.length
          };
          break;

        case 'cancellations':
          // Motivos de cancelación
          const faltaPago = clients.filter(c => c.cancellationType === 'falta_pago').length;
          const solicitud = clients.filter(c => c.cancellationType === 'solicitud_cliente').length;
          const otro = clients.filter(c => c.cancellationType === 'otro' && c.cancellationReason).length;
          
          data = [
            { name: 'Falta de pago', value: faltaPago },
            { name: 'Solicitud cliente', value: solicitud },
            { name: 'Otros', value: otro }
          ].filter(item => item.value > 0);
          
          summary = {
            total: faltaPago + solicitud + otro,
            faltaPago,
            solicitud
          };
          break;

        default:
          data = [];
      }
      
      setReportData({ data, summary });
      
    } catch (error) {
      console.error('Error fetching report:', error);
      setError('Usando datos de ejemplo');
      
      // Datos de ejemplo
      const mockData = {
        powerStatus: {
          data: [
            { name: 'Activo', value: 98, color: '#10B981' },
            { name: 'Sin Luz', value: 23, color: '#F59E0B' },
            { name: 'Desconectado', value: 15, color: '#6B7280' },
            { name: 'Urgente', value: 8, color: '#EF4444' }
          ],
          summary: { total: 144, activo: 98, sinLuz: 23, desconectado: 15, urgente: 8 }
        },
        companies: {
          data: [
            { name: 'Empresa A', value: 45 },
            { name: 'Empresa B', value: 32 },
            { name: 'Empresa C', value: 28 },
            { name: 'Empresa D', value: 21 },
            { name: 'Empresa E', value: 18 }
          ],
          summary: { totalCompanies: 25, topCompany: 'Empresa A', topCount: 45 }
        },
        dueDates: {
          data: [
            { name: 'Vencidos', value: 12 },
            { name: 'Esta semana', value: 18 },
            { name: 'Este mes', value: 34 }
          ],
          summary: { vencidos: 12, proximos: 52, total: 144 }
        },
        cancellations: {
          data: [
            { name: 'Falta de pago', value: 8 },
            { name: 'Solicitud cliente', value: 3 },
            { name: 'Otros', value: 2 }
          ],
          summary: { total: 13, faltaPago: 8, solicitud: 3 }
        }
      };
      
      setReportData(mockData[reportType] || mockData.powerStatus);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format) => {
    alert(`Exportando reporte en formato ${format.toUpperCase()}`);
  };

  const renderChart = () => {
    if (!reportData?.data) return null;

    switch(reportType) {
      case 'powerStatus':
        return (
          <PieChart>
            <Pie
              data={reportData.data}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={120}
              dataKey="value"
            >
              {reportData.data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || COLORS[entry.name] || '#8884d8'} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        );
      
      case 'companies':
        return (
          <BarChart data={reportData.data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" width={100} />
            <Tooltip />
            <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
          </BarChart>
        );
      
      case 'dueDates':
        return (
          <BarChart data={reportData.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      
      case 'cancellations':
        return (
          <PieChart>
            <Pie
              data={reportData.data}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={120}
              dataKey="value"
            >
              {reportData.data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Reportes</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            Análisis de clientes y servicios
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
          >
            <FiDownload className="mr-2" size={16} />
            PDF
          </button>
          <button
            onClick={() => handleExport('excel')}
            className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            <FiDownload className="mr-2" size={16} />
            Excel
          </button>
          <button
            onClick={fetchReportData}
            className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} size={16} />
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Reporte
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="powerStatus">Estado de la Luz</option>
              <option value="companies">Clientes por Compañía</option>
              <option value="dueDates">Próximos Vencimientos</option>
              <option value="cancellations">Cancelaciones</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rango de Fechas
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="week">Última Semana</option>
              <option value="month">Último Mes</option>
              <option value="quarter">Último Trimestre</option>
              <option value="year">Último Año</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-sm text-yellow-700">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {reportType === 'powerStatus' && (
          <>
            <SummaryCard
              title="Total Clientes"
              value={reportData?.summary?.total || 0}
              icon={FiUsers}
              color="blue"
            />
            <SummaryCard
              title="Con Luz"
              value={reportData?.summary?.activo || 0}
              icon={FiSun}
              color="green"
            />
            <SummaryCard
              title="Sin Luz"
              value={reportData?.summary?.sinLuz || 0}
              icon={FiCloudOff}
              color="yellow"
            />
            <SummaryCard
              title="Urgentes"
              value={reportData?.summary?.urgente || 0}
              icon={FiAlertTriangle}
              color="red"
            />
          </>
        )}
        
        {reportType === 'dueDates' && (
          <>
            <SummaryCard
              title="Vencidos"
              value={reportData?.summary?.vencidos || 0}
              icon={FiZap}
              color="red"
            />
            <SummaryCard
              title="Próximos"
              value={reportData?.summary?.proximos || 0}
              icon={FiTrendingUp}
              color="yellow"
            />
          </>
        )}
        
        {reportType === 'cancellations' && (
          <>
            <SummaryCard
              title="Total Cancelaciones"
              value={reportData?.summary?.total || 0}
              icon={FiBarChart2}
              color="red"
            />
            <SummaryCard
              title="Falta de pago"
              value={reportData?.summary?.faltaPago || 0}
              icon={FiAlertTriangle}
              color="orange"
            />
          </>
        )}
      </div>

      {/* Chart */}
      {reportData?.data && reportData.data.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold mb-4">
            {reportType === 'powerStatus' && 'Distribución por Estado de Luz'}
            {reportType === 'companies' && 'Top 10 Compañías por Clientes'}
            {reportType === 'dueDates' && 'Distribución de Vencimientos'}
            {reportType === 'cancellations' && 'Motivos de Cancelación'}
          </h2>
          <div className="w-full h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart()}
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente auxiliar
const SummaryCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    yellow: 'from-yellow-500 to-yellow-600',
    red: 'from-red-500 to-red-600',
    orange: 'from-orange-500 to-orange-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-lg shadow p-4 text-white`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm opacity-90">{title}</p>
          <p className="text-xl font-bold mt-1">{value}</p>
        </div>
        <Icon size={28} className="opacity-75" />
      </div>
    </div>
  );
};

export default Reports;